(() => {
  if (window.KDKPdpPreviewLoader && typeof window.KDKPdpPreviewLoader.init === 'function') {
    window.KDKPdpPreviewLoader.init(document);
    return;
  }

  const loadedScripts = new Map();
  const embedCache = new Map();
  let galleryPending = false;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const optionValue = (variant, index) => String(variant?.['option' + (index + 1)] || variant?.options?.[index] || '').trim();
  const frameLabel = variant => optionValue(variant, 0);
  const isUnframed = value => /sin marco|sans cadre|sem moldura|ohne rahmen|unframed|print only|poster/.test(normalize(value));
  const finishValue = variant => /textur|texture|strukt|leinwand|tela|canvas/.test(normalize(optionValue(variant, 1))) ? 'texture' : 'smooth';
  const sizeValue = variant => normalize(optionValue(variant, 2)).replace(/\s*(cm|centimetros)\b/g, '').replace(/[×*]/g, 'x').replace(/,/g, '.').replace(/\s+/g, '');

  function frameValue(variant) {
    const value = normalize(frameLabel(variant));
    if (isUnframed(value)) return null;
    const type = /gallery|galery|galeri|lienzo/.test(value) ? 'gallery' : /minimal/.test(value) ? 'minimal' : /century/.test(value) ? 'century' : '';
    const color = /nogal|walnut|noyer|walnuss|nogueira/.test(value) ? 'walnut' : /blanco|white|blanc|weiss|weiß|branco/.test(value) ? 'white' : /madera|wood|roble|bois|chene|oak|eiche|holz|madeira|carvalho/.test(value) ? 'wood' : /negro|black|noir|schwarz|preto/.test(value) ? 'black' : '';
    return type && color ? { type, color } : null;
  }

  function eligibleGalleryVariant(variant) {
    const frame = frameValue(variant);
    return Boolean(variant && variant.available !== false && /^\d+$/.test(String(variant.id || '')) &&
      frame && optionValue(variant, 2) && (frame.type !== 'gallery' || finishValue(variant) === 'texture'));
  }

  function resolveGalleryVariant(product, selectedVariantId) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const selected = variants.find(variant => String(variant.id) === String(selectedVariantId));
    if (!selected) return null;
    if (frameValue(selected)) return eligibleGalleryVariant(selected) ? selected : null;
    if (!isUnframed(frameLabel(selected))) return null;
    const candidates = variants.filter(variant => {
      const frame = frameValue(variant);
      return frame?.type === 'minimal' && frame.color === 'black' && eligibleGalleryVariant(variant);
    });
    const score = variant => (sizeValue(variant) === sizeValue(selected) ? 8 : 0) +
      (finishValue(variant) === finishValue(selected) ? 4 : 0) + (finishValue(variant) === 'smooth' ? 1 : 0);
    return candidates.sort((a, b) => score(b) - score(a))[0] || null;
  }

  function loadScriptOnce(src) {
    if (!src) return Promise.resolve();
    if (loadedScripts.has(src)) return loadedScripts.get(src);

    const promise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src.replace(/"/g, '\\"')}"]`);
      if (existing && existing.dataset.kdkLoaded === 'true') {
        resolve();
        return;
      }

      const script = existing || document.createElement('script');
      script.src = src;
      script.async = false;
      script.defer = true;

      const onLoad = () => {
        script.dataset.kdkLoaded = 'true';
        resolve();
      };

      const onError = () => {
        loadedScripts.delete(src);
        script.remove();
        reject(new Error(`No se pudo cargar ${src}`));
      };

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      if (!existing) {
        document.head.appendChild(script);
      }
    });

    loadedScripts.set(src, promise);
    return promise;
  }

  function ensurePreviewAssets() {
    if (ensurePreviewAssets.promise) return ensurePreviewAssets.promise;

    const assets = window.KDK_PREVIEW_ASSETS || {};

    ensurePreviewAssets.promise = Promise.resolve()
      .then(() => window.KdkPreviewSharedScene ? null : loadScriptOnce(assets.sharedScene))
      .then(() => window.KdkProductPreview?.__booted ? null : loadScriptOnce(assets.modal))
      .catch((error) => { ensurePreviewAssets.promise = null; throw error; });

    return ensurePreviewAssets.promise;
  }

  function buildPreviewUrl(baseUrl) {
    const url = new URL(baseUrl || window.location.pathname, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('invalid_product');
    url.searchParams.set('view', 'kdk-preview-embed');
    const previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewId) url.searchParams.set('preview_theme_id', previewId);
    return url.toString();
  }

  function fetchPreviewEmbed(url) {
    const normalizedUrl = buildPreviewUrl(url);
    if (embedCache.has(normalizedUrl)) return embedCache.get(normalizedUrl);

    const promise = fetch(normalizedUrl, {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Respuesta inesperada ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        const template = document.createElement('template');
        template.innerHTML = String(html || '').trim();
        const root = template.content.querySelector('.kdk-pdp-preview');
        if (!root) {
          throw new Error('No se encontró .kdk-pdp-preview en el embed');
        }
        return root.innerHTML;
      })
      .catch((error) => { embedCache.delete(normalizedUrl); throw error; });

    embedCache.set(normalizedUrl, promise);
    return promise;
  }

  function hydrateShell(shell) {
    if (!shell) return Promise.reject(new Error('Shell no disponible'));
    if (shell.dataset.kdkPreviewHydrated === 'true') return Promise.resolve(shell);
    if (shell.__kdkPreviewPromise) return shell.__kdkPreviewPromise;

    const openButton = shell.querySelector('[data-kdk-preview-shell-open]');
    if (openButton) {
      openButton.disabled = true;
      openButton.setAttribute('aria-busy', 'true');
    }

    shell.__kdkPreviewPromise = Promise.all([
      ensurePreviewAssets(),
      fetchPreviewEmbed(shell.dataset.kdkPreviewUrl || window.location.pathname)
    ])
      .then(([, innerHtml]) => {
        shell.classList.add('kdk-pdp-preview');
        shell.innerHTML = innerHtml;
        shell.dataset.kdkPreviewHydrated = 'true';
        shell.removeAttribute('data-kdk-preview-shell');

        if (window.KdkProductPreview && typeof window.KdkProductPreview.init === 'function') {
          window.KdkProductPreview.init(document);
        }

        return shell;
      })
      .catch((error) => {
        console.error('[KDK preview] Error al hidratar el modal', error);
        if (openButton) {
          openButton.disabled = false;
          openButton.removeAttribute('aria-busy');
        }
        throw error;
      })
      .finally(() => {
        delete shell.__kdkPreviewPromise;
      });

    return shell.__kdkPreviewPromise;
  }

  function openPreview(shell) {
    if (!shell) return;

    if (shell.dataset.kdkPreviewHydrated === 'true') {
      shell.querySelector('[data-kdk-preview-open]')?.click();
      return;
    }

    hydrateShell(shell).then((hydrated) => {
      hydrated.querySelector('[data-kdk-preview-open]')?.click();
    }).catch(() => {});
  }

  async function openGallery(options = {}) {
    if (galleryPending) return { status: 'busy', code: 'busy' };
    const shell = options.shell;
    const file = options.file instanceof Blob && options.file.size ? options.file : null;
    const spaceId = String(options.spaceId || '');
    const variantId = String(options.variantId || '');
    const selectedVariantId = String(options.selectedVariantId || variantId);
    if (!shell?.isConnected || !/^\d+$/.test(variantId) || (!file && !spaceId) || (file && spaceId)) {
      return { status: 'error', code: 'invalid_product' };
    }
    if (spaceId && !/^[a-zA-Z0-9_-]{8,90}$/.test(spaceId)) return { status: 'error', code: 'not_found' };
    galleryPending = true;
    let owner, opened = false;
    try {
      if (window.KDK_SPACES_CONFIG?.guidedEnabled !== true || !window.KdkSpaces?.ensureGuided) throw new Error('load_failed');
      const [root, guided] = await Promise.all([
        shell.__kdkProductPreviewer ? Promise.resolve(shell) : hydrateShell(shell),
        window.KdkSpaces.ensureGuided()
      ]);
      owner = root.__kdkProductPreviewer;
      if (!root.isConnected || !owner || owner.cartContext || !guided?.attach) throw new Error('invalid_product');
      const resolvedVariant = resolveGalleryVariant(owner.product, selectedVariantId);
      if (String(resolvedVariant?.id || '') !== variantId || !window.KdkSpacesProducts.options(owner.product).some(row => row.variantId === variantId)) {
        throw new Error('gallery_variant_unavailable');
      }
      if (owner.aiProcessing || owner._photoLoading || owner._guided?.loading) return { status: 'busy', code: 'busy', owner };
      owner._guidedPendingConsumed = true;
      owner._kdkGalleryPreparing = true;
      if (!owner.__kdkGalleryObserver) {
        owner.__kdkGalleryObserver = new MutationObserver(() => {
          if (owner.modalEl.hidden) {
            owner._guided?.endGalleryEntry?.();
            owner._kdkGalleryPreparing = false;
          }
        });
        owner.__kdkGalleryObserver.observe(owner.modalEl, { attributes: true, attributeFilter: ['hidden'] });
      }
      window.KdkPreviewAnalytics?.beginGallery(owner);
      const opening = owner.open();
      opened = !owner.modalEl.hidden;
      owner._setMode('ai', { keepResult: true });
      await opening;
      if (owner.modalEl.hidden) return { status: 'cancelled', code: 'cancelled', owner };
      owner._setMode('ai', { keepResult: true });
      window.KdkSpacesUI.attach(owner);
      const editor = guided.attach(owner);
      await editor.ready;
      const until = Date.now() + 25000;
      while (editor.loading || editor.capacityPending || owner._photoLoading) {
        if (owner.modalEl.hidden) return { status: 'cancelled', code: 'cancelled', owner, editor };
        if (Date.now() > until) throw new Error('load_failed');
        await new Promise(resolve => setTimeout(resolve, 40));
      }
      if (owner.modalEl.hidden) return { status: 'cancelled', code: 'cancelled', owner, editor };
      const result = await editor.beginGallery({ file, spaceId, variantId, source: options.source });
      if (result.status === 'ready' && !owner.modalEl.hidden) {
        editor.cv?.focus({ preventScroll: true });
      }
      return { ...result, owner, editor };
    } catch (error) {
      if (owner && !opened) window.KdkPreviewAnalytics?.endGallery(owner);
      if (owner && !owner.modalEl.hidden) {
        const code = error.code || error.message;
        const message = window.KDK_SPACES_I18N?.[code] || window.KDK_SPACES_CONFIG?.loadError || owner.t('msg_ai_error');
        owner._setStatus(owner.aiStatusEl, message, 'error');
      }
      return { status: 'error', code: error.code || error.message || 'load_failed', owner };
    } finally {
      if (owner) owner._kdkGalleryPreparing = false;
      galleryPending = false;
    }
  }

  function init(scope = document) {
    const shells = scope.querySelectorAll ? scope.querySelectorAll('[data-kdk-preview-shell]:not([data-kdk-preview-shell-bound])') : [];

    shells.forEach((shell) => {
      shell.setAttribute('data-kdk-preview-shell-bound', 'true');
      const button = shell.querySelector('[data-kdk-preview-shell-open]');
      if (!button) return;

      button.addEventListener('click', () => openPreview(shell));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  window.KDKPdpPreviewLoader = {
    init,
    hydrateShell,
    openPreview
  };
  window.KdkGalleryPreviewBridge = { open: openGallery, resolveVariant: resolveGalleryVariant, frameLabel };
})();

